import { assignZIndexes } from "@/utils/appointment-utils";
import { useDroppable } from "@dnd-kit/core";
import { useMemo } from "react";
import styled from "styled-components";
import { useCalendarStore } from "../../store/useCalendarStore";
import AppointmentBlock from "./AppointmentBlock/AppointmentBlock";
import dayjs from "dayjs";

const AppointmentsColumn = ({ date, userId, usersInOrder }) => {
  const allAppointments = useCalendarStore(
    (state) => state.calendarData.allAppointments,
  );
  const showOnlyFixedAppointments = useCalendarStore(
    (state) => state.showOnlyFixedAppointments,
  );

  const previousUserId = usersInOrder[usersInOrder.indexOf(userId) - 1];
  const { calendarData } = useCalendarStore((state) => state);
  const filteredAppointments = useMemo(
    () =>
      (
        allAppointments.filter(
          (appointment) =>
            dayjs(date).isBetween(
              dayjs(`${appointment.date}T${appointment.startTime}`),
              dayjs(`${appointment.date}T${appointment.startTime}`).add(
                appointment.duration,
                "minutes",
              ),
              "day",
              "[]",
            ) &&
            appointment.userIds.includes(userId) &&
            (!showOnlyFixedAppointments ||
              appointment.fixedAppointmentId != null),
        ) || []
      ).map((a) => {
        //calculate number of next consecutive resources

        const prevUsers = usersInOrder.slice(0, usersInOrder.indexOf(userId));
        const nextAppUsers = a.userIds.filter((u) => !prevUsers.includes(u));
        const nextUsersInOrder = usersInOrder.slice(
          usersInOrder.indexOf(userId),
        );

        let numberOfNextUsersInOrder = 0;

        for (
          let i = 0;
          i < Math.min(nextAppUsers.length, nextUsersInOrder.length);
          i++
        ) {
          if (nextAppUsers.includes(nextUsersInOrder[i])) {
            numberOfNextUsersInOrder++;
          } else {
            break;
          }
        }

        return {
          ...a,
          isOnPreviousUser: a.userIds?.includes(previousUserId),
          numberOfNextUsers: numberOfNextUsersInOrder,
        };
      }),
    [
      allAppointments,
      date,
      userId,
      usersInOrder,
      previousUserId,
      showOnlyFixedAppointments,
    ],
  );

  const appointments = useMemo(
    () => assignZIndexes(filteredAppointments, calendarData.timeStep),
    [filteredAppointments, calendarData.timeStep],
  );

  const { setNodeRef } = useDroppable({
    id: `appointmentsColumn-${date}-${userId}`,
    data: { date, userId },
  });

  return (
    <Wrapper ref={setNodeRef} data-slot-surface>
      {appointments.flatMap((appointment) => {
        if (appointment.isOnPreviousUser) return [];
        return [
          <AppointmentBlock
            appointment={appointment}
            key={appointment.appointmentId}
            user={userId}
            usersInOrder={usersInOrder}
            columnDate={date}
          ></AppointmentBlock>,
        ];
      })}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  width: 100%;
  height: 100%;

  position: absolute;

  &.hovering {
    background-color: rgba(0, 0, 0, 0.05);
  }
`;

export default AppointmentsColumn;
